import { useCallback, useEffect, useRef, type ReactNode } from "react";

interface ConfirmDialogProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  title: ReactNode;
  description: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** "danger" uses rust-colored confirm button; "default" uses tide. */
  variant?: "danger" | "default";
  /** When true, the confirm button shows a loading state. */
  confirming?: boolean;
}

/**
 * Accessible confirmation dialog with focus trap, Escape-to-close,
 * backdrop click, and focus restoration.
 *
 * Extracted from the three inline confirmation dialogs in the page
 * canvas editor (delete page, discard draft, unsaved-changes blocker).
 */
export function ConfirmDialog({
  open,
  onConfirm,
  onCancel,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "default",
  confirming = false,
}: ConfirmDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<Element | null>(null);
  const titleId = useRef(
    `confirm-dialog-title-${Math.random().toString(36).slice(2, 8)}`,
  ).current;

  // Capture the element that had focus when the dialog opened, and
  // restore it when it closes.
  useEffect(() => {
    if (open) {
      triggerRef.current = document.activeElement;
      // Focus the dialog container so keyboard events land inside it
      requestAnimationFrame(() => dialogRef.current?.focus());
    } else if (triggerRef.current) {
      const el = triggerRef.current;
      triggerRef.current = null;
      if (el instanceof HTMLElement) {
        requestAnimationFrame(() => el.focus());
      }
    }
  }, [open]);

  // Prevent body scroll while dialog is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Escape key
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCancel();
      }
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onCancel]);

  // Focus trap: cycle tab through dialog controls only
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const container = dialogRef.current;
      if (!container) return;
      const focusable = container.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [],
  );

  if (!open) return null;

  const confirmClass =
    variant === "danger"
      ? "border border-rust bg-rust text-white hover:bg-rust-d disabled:opacity-45"
      : "border border-tide bg-tide text-white hover:bg-tide-d disabled:opacity-45";

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/30 motion-safe:animate-[fadein_0.12s_ease]"
      onClick={onCancel}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        onClick={(e) => e.stopPropagation()}
        className="bg-paper-hi border border-ink-6 rounded-[14px] p-5 shadow-lift w-[340px] outline-none motion-safe:animate-[rise_0.15s_cubic-bezier(0.3,1.2,0.4,1)]"
      >
        <h4 id={titleId} className="text-[15px] font-semibold mb-2">
          {title}
        </h4>
        <p className="text-[13px] text-ink-3 leading-relaxed mb-4">
          {description}
        </p>
        <div className="flex gap-2 justify-end">
          <button
            onClick={onCancel}
            disabled={confirming}
            className="text-[13px] font-semibold rounded-[10px] px-3.5 py-2 border border-ink-6 bg-paper-hi text-ink-2 hover:bg-paper-2 transition-colors"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={confirming}
            className={`text-[13px] font-semibold rounded-[10px] px-3.5 py-2 transition-colors ${confirmClass}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
